import React from "react"
import { observer } from "mobx-react-lite"
import {
  View,
  ViewStyle,
  TextStyle,
  Dimensions,
  Linking,
  ListRenderItem,
  TouchableOpacity,
  FlatList
} from "react-native"
import { StackScreenProps } from "@react-navigation/stack"
import { Button, Screen, Text, Svg } from "@app/components"
import { TextProps } from "@app/components/text/text.props"
import { color, spacing } from "@app/theme"
import { Footer } from "@app/components/footer/footer"
import { Feather } from "@expo/vector-icons"
import { textPresets } from "@app/components/button/button.presets"
import { RootNavigatorParamList } from "@app/navigators/navigator-params"

type ListItem = {
  text: any
  preset: TextProps["preset"]
}

const SUPPORT_EMAIL = "kundtjanst@veckanse.com"

type CookieScreenProps = StackScreenProps<RootNavigatorParamList, "Cookie">
export const CookieScreen = observer((props: CookieScreenProps) => {
  const goHome = () =>
    props.navigation.navigate("Tabs", { screen: "Home", params: { screen: "Offers" } })

  const renderItem: ListRenderItem<ListItem> = ({ item }) => {
    switch (item.preset) {
      case "bold":
        return (
          <Text preset={item.preset} style={LIST_ITEM_BOLD}>
            {item.text}
          </Text>
        )
      default:
        return (
          <Text preset={item.preset} style={LIST_ITEM_DEFAULT}>
            {item.text}
          </Text>
        )
    }
  }

  const mailLink = (
    <TouchableOpacity onPress={() => Linking.openURL(`mailto:${SUPPORT_EMAIL}`)}>
      <Text style={LINK} text={SUPPORT_EMAIL} />
    </TouchableOpacity>
  )

  const consentLink = (
    <TouchableOpacity onPress={() => props.navigation.navigate("Consent")}>
      <Text style={LINK} text="Samtycke – Klickspårning i nyhetsbrev" />
    </TouchableOpacity>
  )

  return (
    <Screen style={ROOT} preset="scroll">
      <View style={PAGE_WRAPPER}>
        <View style={BACKGROUND_IMAGE}>
          <Svg preset="PinkBlob" />
        </View>
        <Button preset="back" onPress={goHome}>
          <Feather style={textPresets.back} name="chevron-left" />
          <Text style={textPresets.back} text="Tillbaka" />
        </Button>
        <Text style={HEADER_LARGE} preset="headerLarge" text="Cookiepolicy" />
        <Text preset="default" text="Hur VeckansE använder cookies och liknande tekniker" />
        <Text
          preset="default"
          text="Cartero Digital Marketing AB | Org.nr 556857–4262 | Box 5077, 102 42 Stockholm"
        />
        <Text preset="default" text="Gäller från: 2026-04-20" />
        <FlatList
          data={[
            { preset: "bold", text: "1. Vad är cookies?" },
            {
              preset: "default",
              text: "Cookies är små textfiler som lagras på din enhet när du besöker veckanse.se. De används för att webbplatsen ska fungera korrekt, för att förbättra din upplevelse och för marknadsföringsändamål. Vi använder även liknande tekniker som pixeltaggar och lokal lagring."
            },
            { preset: "bold", text: "2. Rättslig grund" },
            {
              preset: "default",
              text: "Vi hanterar cookies i enlighet med GDPR och lagen om elektronisk kommunikation (LEK):"
            },
            {
              preset: "default",
              text: "• Nödvändiga cookies: används med stöd av berättigat intresse (art. 6.1(f)) — dessa krävs för att webbplatsen ska fungera och kräver inte ditt samtycke"
            },
            {
              preset: "default",
              text: "• Alla övriga cookies: används endast efter ditt aktiva samtycke (art. 6.1(a) GDPR) via vår cookie-banner. Du kan när som helst ändra dina val."
            },
            { preset: "bold", text: "3. Cookiekategorier" },
            { preset: "bold", text: "3.1 Nödvändiga cookies" },
            {
              preset: "default",
              text: "Dessa cookies är nödvändiga för att webbplatsen ska fungera. De kan inte stängas av."
            },
            {
              preset: "default",
              text: "• session_id — Håller din session aktiv (veckanse.se, session)"
            },
            {
              preset: "default",
              text: "• csrf_token — Skydd mot cross-site request forgery (veckanse.se, session)"
            },
            {
              preset: "default",
              text: "• cookie_consent — Sparar dina cookie-inställningar (veckanse.se, 12 månader)"
            },
            { preset: "bold", text: "3.2 Funktionella cookies" },
            {
              preset: "default",
              text: "Dessa cookies möjliggör förbättrad funktionalitet och personalisering, t.ex. att komma ihåg dina preferenser."
            },
            {
              preset: "default",
              text: "• user_prefs — Sparar dina erbjudandekategorier (veckanse.se, 6 månader)"
            },
            {
              preset: "default",
              text: "• location — Sparar din valda plats för lokala erbjudanden (veckanse.se, 30 dagar)"
            },
            { preset: "bold", text: "3.3 Analys- och statistikcookies" },
            {
              preset: "default",
              text: "Dessa cookies hjälper oss förstå hur besökare använder webbplatsen. Informationen är aggregerad och anonym."
            },
            {
              preset: "default",
              text: "• _ga — Skiljer mellan sessioner, Google Analytics (Google, 2 år)"
            },
            {
              preset: "default",
              text: "• _ga_* — Kopplar sessioner till specifik GA4-egendom (Google, 2 år)"
            },
            {
              preset: "default",
              text: "• _gid — Identifierar unik session (Google, 24 timmar)"
            },
            { preset: "bold", text: "3.4 Marknadsföringscookies" },
            {
              preset: "default",
              text: "Dessa cookies används för att visa relevanta annonser och mäta effektiviteten av marknadsföringskampanjer."
            },
            {
              preset: "default",
              text: "• _fbp — Facebook Pixel, spårar konverteringar (Meta/Facebook, 90 dagar)"
            },
            {
              preset: "default",
              text: "• fr — Facebook, levererar och mäter annonser (Meta/Facebook, 90 dagar)"
            },
            { preset: "bold", text: "4. Tredjepartsleverantörer" },
            {
              preset: "default",
              text: "Vi använder följande tredjepartstjänster som kan placera egna cookies:"
            },
            {
              preset: "default",
              text: "• Google Analytics: analyser av webbtrafik. Googles sekretesspolicy: policies.google.com/privacy"
            },
            {
              preset: "default",
              text: "• Meta (Facebook): marknadsföring och konverteringsspårning. Metas datapolicy: facebook.com/policy"
            },
            {
              preset: "default",
              text: "Dessa leverantörer ansvarar för sin egen behandling av personuppgifter. Vi rekommenderar att du läser deras respektive integritetspolicyer."
            },
            { preset: "bold", text: "5. Ditt samtycke och dina val" },
            {
              preset: "default",
              text: "När du besöker veckanse.se för första gången visas en cookie-banner där du kan:"
            },
            { preset: "default", text: "• Acceptera alla cookies" },
            { preset: "default", text: "• Acceptera endast nödvändiga cookies" },
            { preset: "default", text: "• Göra ett anpassat val per kategori" },
            {
              preset: "default",
              text: "Du kan när som helst ändra dina inställningar via länken 'Cookieinställningar' i sidfoten på veckanse.se. Det är lika enkelt att återkalla samtycket som att ge det."
            },
            {
              preset: "default",
              text: "Du kan också hantera cookies via din webbläsare. Observera att blockering av nödvändiga cookies kan påverka webbplatsens funktionalitet."
            },
            { preset: "bold", text: "6. Klickspårning i nyhetsbrev" },
            {
              preset: "default",
              text: (
                <>
                  Klickspårning i våra nyhetsbrev (t.ex. om och när ett nyhetsbrev öppnats och vilka
                  länkar du klickat på) sker med ett separat samtycke och regleras i ett eget
                  dokument: {consentLink}.
                </>
              )
            },
            { preset: "bold", text: "7. Kontakt" },
            { preset: "default", text: "Cartero Digital Marketing AB" },
            { preset: "default", text: "Box 5077, 102 42 Stockholm" },
            {
              preset: "default",
              text: (
                <>
                  E-post: {mailLink}
                </>
              )
            },
            { preset: "default", text: "Denna cookiepolicy gäller fr.o.m. 2026-04-20" }
          ]}
          renderItem={renderItem}
        />
        <View></View>
      </View>
      <Footer />
    </Screen>
  )
})

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  minHeight: Dimensions.get("window").height
}

const LIST_ITEM_BOLD: ViewStyle = {
  marginTop: spacing[4],
  marginBottom: spacing[2]
}
const LIST_ITEM_DEFAULT: ViewStyle = {
  marginLeft: spacing[3],
  marginBottom: spacing[2]
}

const PAGE_WRAPPER: ViewStyle = {
  padding: 24,
  paddingBottom: 80,
  maxWidth: 840,
  minHeight: Dimensions.get("window").height,
  alignSelf: "center"
}

const BACKGROUND_IMAGE: ViewStyle = {
  height: 500,
  width: 580,
  position: "absolute",
  top: -100,
  left: -200,
  transform:
    Dimensions.get("window").width > 768
      ? [{ scale: 2 }, { rotate: "45deg" }]
      : [{ scale: 1 }, { rotate: "45deg" }],
  zIndex: -1
}

const HEADER_LARGE: TextStyle = {
  marginTop: spacing[5],
  marginBottom: spacing[5]
}

const LINK: TextStyle = {
  color: color.palette.pink3
}
